import { type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import ErrorBoundary from './ErrorBoundary';

/** Friendly fallback shown when a routed page throws during render. */
const ErrorFallback = () => {
  const { t } = useTranslation();
  return (
    <div className="mx-auto flex min-h-[50vh] max-w-md flex-col items-center justify-center gap-3 px-4 py-10 text-center">
      <h1 className="text-xl font-semibold text-foreground">{t('error_boundary.title')}</h1>
      <p className="text-sm text-muted-foreground">{t('error_boundary.body')}</p>
      <Button onClick={() => window.location.reload()}>{t('error_boundary.reload')}</Button>
    </div>
  );
};

/**
 * Catches render-time crashes in the routed page tree and shows a recoverable
 * fallback instead of unmounting the whole app. Resets automatically when the
 * path changes, so navigating to another page clears the error.
 */
const RouteErrorBoundary = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  return (
    <ErrorBoundary resetKey={location.pathname} label="route" fallback={<ErrorFallback />}>
      {children}
    </ErrorBoundary>
  );
};

export default RouteErrorBoundary;
